import { Fragment, useState } from "react";
import { IconoFlechaIzquierda, IconoFlechaDerecha } from "../../AuditoriaClinica/rediseno/AuditIconos";
import { IconoChevronDerecha, IconoChevronAbajo } from "../Iconos";
import { Casilla } from "../Campo";
import "./DataTable.css";

/* ============================================================================
   REDISENO-SMARTMEDIC · FASE 3 — DataTable

   Tabla reutilizable para TODAS las pantallas. Se construye sobre `<table>`
   propio en vez de sobre el `Table` de antd a propósito: la anatomía del
   rediseño (cabecera de 40px, fila de 58px, truncado con `title`, celda doble,
   barra de 3px en la fila activa) exigiría pelearse con los estilos internos
   de antd en cada pantalla. Aquí se declara una vez.

   No sabe nada de ninguna pantalla: recibe columnas, filas y estados. Las
   reglas de columnas viven en `piezas.js` para poder usarse al declararlas.

   Contrato de columna:
     { key, titulo, ancho?, alinear?: "izquierda"|"centro"|"derecha",
       render?: (fila) => ReactNode, ordenable?: bool, fijadaPor?: string }
   ========================================================================== */

const claseAlineacion = (alinear, prefijo) => {
    if (alinear === "derecha") return ` ${prefijo}--derecha`;
    if (alinear === "centro") return ` ${prefijo}--centro`;
    return "";
};

function Flecha({ direccion, activa }) {
    return (
        <span className={`dt__orden-flecha${activa ? " is-activa" : ""}`} aria-hidden="true">
            {direccion === "desc" ? "▼" : "▲"}
        </span>
    );
}

function FilasSkeleton({ columnas, filas = 6 }) {
    // Skeleton con la FORMA FINAL de la tabla: al llegar los datos nada salta.
    return Array.from({ length: filas }).map((_, i) => (
        <tr className="dt__tr" key={`skel-${i}`} data-testid="dt-skeleton-fila">
            {columnas.map((col) => (
                <td className="dt__td" key={col.key}>
                    <div className="dt__skel" style={{ width: `${45 + ((i * 13 + col.key.length * 7) % 45)}%` }} />
                </td>
            ))}
        </tr>
    ));
}

function DataTable({
    columnas,
    filas,
    filaKey = "id",
    cargando = false,
    error = null,
    onReintentar,
    filaActiva = null,
    onFilaClick,
    orden,
    onOrdenar,
    paginacion,
    hayFiltrosAplicados = false,
    onLimpiarFiltros,
    textoVacioSinFiltros = "Todavía no hay datos.",
    // Salidas ofrecidas cuando la tabla está vacía SIN filtros aplicados:
    //   [{ label, onClick, primaria? }]
    // Opcional: sin ellas el vacío se comporta exactamente como antes.
    accionesVacio = [],
    etiquetaTabla,
    // Detalle por fila (opcional):
    //   expansion = { render: (fila) => ReactNode, expandible?: (fila) => bool }
    // Se pinta como una columna mas, generada aqui, para que colgroup,
    // cabecera, skeleton y colSpan sigan cuadrando solos.
    expansion = null,
    // Seleccion por fila (opcional):
    //   seleccion = { claves, onCambio, deshabilitada?, etiquetaFila? }
    // Misma idea: una columna mas generada aqui.
    seleccion = null,
}) {
    const datos = filas || [];
    const clicable = typeof onFilaClick === "function";

    // Filas desplegadas. El estado vive AQUI: que un detalle este abierto no es
    // un dato de la pantalla, y ninguna deberia tener que guardarlo.
    const [desplegadas, setDesplegadas] = useState([]);

    const columnaExpansion = {
        key: "__expansion",
        titulo: "",
        ancho: 40,
        alinear: "centro",
        render: (fila) => {
            const clave = fila[filaKey];
            const puede = expansion.expandible ? expansion.expandible(fila) : true;
            if (!puede) return null;
            const abierta = desplegadas.includes(clave);
            const Chevron = abierta ? IconoChevronAbajo : IconoChevronDerecha;
            return (
                <button
                    type="button"
                    className="dt__desplegar"
                    aria-expanded={abierta}
                    aria-label={abierta ? "Ocultar detalle" : "Ver detalle"}
                    data-testid={`dt-desplegar-${clave}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        setDesplegadas((actuales) =>
                            actuales.includes(clave) ? actuales.filter((k) => k !== clave) : [...actuales, clave]
                        );
                    }}
                >
                    <Chevron size={15} />
                </button>
            );
        },
    };

    const marcadas = seleccion ? seleccion.claves || [] : [];
    const seleccionables = seleccion
        ? datos.filter((f) => !(seleccion.deshabilitada && seleccion.deshabilitada(f)))
        : [];
    const todasMarcadas =
        seleccionables.length > 0 && seleccionables.every((f) => marcadas.includes(f[filaKey]));

    const alternar = (fila, marcar) => {
        const clave = fila[filaKey];
        const claves = marcar ? [...marcadas, clave] : marcadas.filter((k) => k !== clave);
        seleccion.onCambio(
            claves,
            datos.filter((f) => claves.includes(f[filaKey]))
        );
    };

    const alternarTodas = () => {
        if (todasMarcadas) return seleccion.onCambio([], []);
        const filasSel = seleccionables;
        seleccion.onCambio(filasSel.map((f) => f[filaKey]), filasSel);
    };

    const columnaSeleccion = {
        key: "__seleccion",
        titulo: "",
        ancho: 44,
        alinear: "centro",
        tituloRender: () => (
            <Casilla
                etiqueta="Seleccionar todo lo visible"
                marcada={todasMarcadas}
                disabled={seleccionables.length === 0}
                onChange={alternarTodas}
            />
        ),
        render: (fila) => (
            <Casilla
                etiqueta={
                    seleccion.etiquetaFila ? seleccion.etiquetaFila(fila) : `Seleccionar fila ${fila[filaKey]}`
                }
                marcada={marcadas.includes(fila[filaKey])}
                disabled={Boolean(seleccion.deshabilitada && seleccion.deshabilitada(fila))}
                onChange={(marcar) => alternar(fila, marcar)}
            />
        ),
    };

    // FUSION produccion_antes + feat/ui-factura-crear: las dos funcionalidades
    // se anadieron a este componente por separado, cada una con su columna al
    // principio, y cada una la usa una pantalla real -- `seleccion` en Facturas
    // por cobrar, `expansion` en Planes y Promociones. Se conservan LAS DOS.
    //
    // Orden: primero la casilla, despues el desplegable. Es el orden habitual
    // y hoy ninguna tabla usa ambas, asi que no cambia nada de lo existente.
    // Todos los `colSpan` usan `cols.length`, por lo que siguen cuadrando solos.
    const cols = [
        ...(seleccion ? [columnaSeleccion] : []),
        ...(expansion ? [columnaExpansion] : []),
        ...(columnas || []),
    ];

    const cuerpo = () => {
        if (cargando) return <FilasSkeleton columnas={cols} />;
        if (error) {
            return (
                <tr>
                    <td className="dt__mensaje" colSpan={cols.length} data-testid="dt-error">
                        <div className="dt__mensaje-titulo">No se pudo cargar la información</div>
                        <div>{error}</div>
                        {onReintentar && (
                            <div className="dt__mensaje-acciones">
                                {/* Reintentar NO pierde los filtros: se vuelve a
                                    pedir exactamente la misma vista. */}
                                <button type="button" className="dt__btn" onClick={onReintentar} data-testid="dt-reintentar">
                                    Reintentar
                                </button>
                            </div>
                        )}
                    </td>
                </tr>
            );
        }
        if (datos.length === 0) {
            return (
                <tr>
                    <td className="dt__mensaje" colSpan={cols.length} data-testid="dt-vacio">
                        {/* "No hay resultados con estos filtros" y "todavía no hay
                            datos" son cosas distintas y llevan a acciones distintas. */}
                        <div className="dt__mensaje-titulo">
                            {hayFiltrosAplicados ? "Sin resultados para estos filtros" : "Sin datos"}
                        </div>
                        <div>
                            {hayFiltrosAplicados
                                ? "Prueba a quitar alguno de los filtros aplicados."
                                : textoVacioSinFiltros}
                        </div>
                        {hayFiltrosAplicados && onLimpiarFiltros && (
                            <div className="dt__mensaje-acciones">
                                <button type="button" className="dt__btn" onClick={onLimpiarFiltros} data-testid="dt-limpiar">
                                    Limpiar filtros
                                </button>
                            </div>
                        )}
                        {/* Un vacío de verdad (no de filtros) suele tener UNA
                            salida obvia -- "este tarifario no tiene nada:
                            créalo o impórtalo". Ofrecerla aquí evita que el
                            usuario tenga que subir a buscar el botón. */}
                        {!hayFiltrosAplicados && accionesVacio.length > 0 && (
                            <div className="dt__mensaje-acciones">
                                {accionesVacio.map((accion) => (
                                    <button
                                        key={accion.label}
                                        type="button"
                                        className={`dt__btn${accion.primaria ? " dt__btn--primario" : ""}`}
                                        onClick={accion.onClick}
                                    >
                                        {accion.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </td>
                </tr>
            );
        }
        return datos.map((fila) => {
            const clave = fila[filaKey];
            const activa = filaActiva !== null && filaActiva !== undefined && clave === filaActiva;
            const desplegada = expansion && desplegadas.includes(clave);
            return (
                <Fragment key={clave}>
                <tr
                    className={`dt__tr${clicable ? " dt__tr--clicable" : ""}${activa ? " is-activa" : ""}`}
                    data-testid={`dt-fila-${clave}`}
                    tabIndex={clicable ? 0 : undefined}
                    onClick={clicable ? () => onFilaClick(fila) : undefined}
                    onKeyDown={
                        clicable
                            ? (e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      onFilaClick(fila);
                                  }
                              }
                            : undefined
                    }
                >
                    {cols.map((col) => (
                        <td
                            className={`dt__td${claseAlineacion(col.alinear, "dt__td")}${col.mono ? " dt__td--mono" : ""}`}
                            key={col.key}
                        >
                            {col.render ? col.render(fila) : fila[col.key]}
                        </td>
                    ))}
                </tr>
                {desplegada && (
                    <tr className="dt__tr dt__tr--detalle" data-testid={`dt-detalle-${clave}`}>
                        <td className="dt__td dt__td--detalle" colSpan={cols.length}>
                            {expansion.render(fila)}
                        </td>
                    </tr>
                )}
                </Fragment>
            );
        });
    };

    const p = paginacion;
    const desde = p && p.total > 0 ? (p.page - 1) * p.pageSize + 1 : 0;
    const hasta = p ? Math.min(p.page * p.pageSize, p.total) : 0;

    return (
        <div className="dt" data-testid="dt">
            <div className="dt__scroll">
                <table className="dt__tabla" aria-label={etiquetaTabla}>
                    <colgroup>
                        {cols.map((col) => (
                            <col key={col.key} style={col.ancho ? { width: col.ancho } : undefined} />
                        ))}
                    </colgroup>
                    <thead>
                        <tr>
                            {cols.map((col) => {
                                const activa = orden && orden.campo === col.key;
                                return (
                                    <th
                                        className={`dt__th${claseAlineacion(col.alinear, "dt__th")}`}
                                        key={col.key}
                                        scope="col"
                                        aria-sort={activa ? (orden.direccion === "desc" ? "descending" : "ascending") : undefined}
                                    >
                                        {col.tituloRender ? (
                                            col.tituloRender()
                                        ) : col.ordenable && onOrdenar ? (
                                            <button
                                                type="button"
                                                className="dt__orden"
                                                onClick={() => onOrdenar(col.key)}
                                                data-testid={`dt-ordenar-${col.key}`}
                                            >
                                                {col.titulo}
                                                <Flecha direccion={activa ? orden.direccion : "asc"} activa={activa} />
                                            </button>
                                        ) : (
                                            col.titulo
                                        )}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>{cuerpo()}</tbody>
                </table>
            </div>

            {p && (
                <div className="dt__pie" data-testid="dt-pie">
                    <span data-testid="dt-rango">
                        {p.total > 0 ? `Mostrando ${desde}–${hasta} de ${p.total}` : "Sin resultados"}
                    </span>
                    <div className="dt__pie-derecha">
                        <select
                            className="dt__select"
                            value={p.pageSize}
                            onChange={(e) => p.onCambio(1, Number(e.target.value))}
                            aria-label="Registros por página"
                            data-testid="dt-tamano"
                        >
                            {(p.tamanos || [10, 25, 50, 100]).map((t) => (
                                <option key={t} value={t}>
                                    {t} por página
                                </option>
                            ))}
                        </select>
                        <button
                            type="button"
                            className="dt__btn"
                            disabled={p.page <= 1}
                            onClick={() => p.onCambio(p.page - 1, p.pageSize)}
                            data-testid="dt-anterior"
                        >
                            <IconoFlechaIzquierda size={13} /> Anterior
                        </button>
                        <button
                            type="button"
                            className="dt__btn"
                            disabled={hasta >= p.total}
                            onClick={() => p.onCambio(p.page + 1, p.pageSize)}
                            data-testid="dt-siguiente"
                        >
                            Siguiente <IconoFlechaDerecha size={13} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DataTable;
